import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService, UpdateProgramData } from '../services/api';
import toast from 'react-hot-toast';

export const usePrograms = (visible?: boolean) => {
  return useQuery({
    queryKey: ['programs', visible],
    queryFn: () => apiService.getPrograms(visible),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1, // Only retry once
    retryDelay: 1000, // Wait 1 second before retry
  });
};

export const useProgram = (id: string) => {
  return useQuery({
    queryKey: ['program', id],
    queryFn: () => apiService.getProgram(id),
    enabled: !!id,
    refetchOnWindowFocus: false,
    retry: 1,
    retryDelay: 1000,
  });
};

export const useUpdateProgram = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateProgramData }) =>
      apiService.updateProgram(id, data),
    onSuccess: (updatedProgram) => {
      queryClient.invalidateQueries({ queryKey: ['programs'] });
      queryClient.setQueryData(['program', updatedProgram._id], updatedProgram);
      toast.success('Program updated successfully!');
    },
    onError: (error: any) => {
      toast.error(`Failed to update program: ${error.response?.data?.message || error.message}`);
    },
  });
};

export const useToggleVisibility = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => apiService.toggleVisibility(id),
    onSuccess: (updatedProgram) => {
      queryClient.invalidateQueries({ queryKey: ['programs'] });
      queryClient.setQueryData(['program', updatedProgram._id], updatedProgram);
      toast.success(`Program ${updatedProgram.visibility ? 'activated' : 'hidden'} successfully!`);
    },
    onError: (error: any) => {
      toast.error(`Failed to toggle visibility: ${error.response?.data?.message || error.message}`);
    },
  });
};

export const useDeleteProgram = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => apiService.deleteProgram(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['programs'] });
      toast.success('Program deleted successfully!');
    },
    onError: (error: any) => {
      toast.error(`Failed to delete program: ${error.response?.data?.message || error.message}`);
    },
  });
};

export const useSearchPrograms = () => {
  return useMutation({
    mutationFn: (name: string) => apiService.searchPrograms(name),
    onError: (error: any) => {
      toast.error(`Search failed: ${error.response?.data?.message || error.message}`);
    },
  });
};

export const useProgramStats = () => {
  return useQuery({
    queryKey: ['programStats'],
    queryFn: () => apiService.getStats(),
    refetchOnWindowFocus: false,
    staleTime: 10 * 60 * 1000, // 10 minutes
    retry: 1,
    retryDelay: 1000,
  });
};

export const useArchivedPrograms = () => {
  return useQuery({
    queryKey: ['archivedPrograms'],
    queryFn: () => apiService.getArchivedPrograms(),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
    retryDelay: 1000,
  });
};

// Priority Management Hooks
export const useProgramsByPriority = () => {
  return useQuery({
    queryKey: ['programs', 'by-priority'],
    queryFn: () => apiService.getProgramsByPriority(),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
    retryDelay: 1000,
  });
};

export const useUpdatePriority = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, priority }: { id: string; priority: number }) =>
      apiService.updatePriority(id, priority),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['programs'] });
      toast.success('Priority updated successfully!');
    },
    onError: (error: any) => {
      toast.error(`Failed to update priority: ${error.response?.data?.message || error.message}`);
    },
  });
};

export const useBulkUpdatePriorities = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (updates: Array<{ programId: string; priority: number }>) =>
      apiService.bulkUpdatePriorities(updates),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['programs'] });
      toast.success(`${result.updated} program priorities updated!`);
    },
    onError: (error: any) => {
      toast.error(`Failed to update priorities: ${error.response?.data?.message || error.message}`);
    },
  });
};

export const useUpdateCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, category }: { id: string; category: string }) =>
      apiService.updateCategory(id, category),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['programs'] });
      toast.success('Category updated successfully!');
    },
    onError: (error: any) => {
      toast.error(`Failed to update category: ${error.response?.data?.message || error.message}`);
    },
  });
};

export const useCategories = () => {
  return useQuery({
    queryKey: ['categories'],
    queryFn: () => apiService.getCategories(),
    refetchOnWindowFocus: false,
    staleTime: 10 * 60 * 1000, // 10 minutes
    retry: 1,
    retryDelay: 1000,
  });
}; 